/**
 * The specialty catalogue.
 *
 * rev v11.0.0 · Phase 2 / C1
 *
 * The complete set of clinical workspaces the platform delivers. Each entry
 * is a self-contained manifest; adding a specialty is adding one file and
 * one line here.
 *
 * The list is explicit rather than discovered at runtime: the frontend needs
 * the set to build its route tree and its lazy chunks, and a typo must fail
 * the build rather than produce a 404 at a hospital.
 *
 * VOCABULARY NOTE
 * ---------------
 * Two vocabularies coexist in the platform and this file reconciles them:
 *
 *   - 15 catalogue slugs (kebab-case) — the NGAP Maroc taxonomy, used by
 *     apps/web/src/lib/ngap-specialities.ts and by docs/billing/ngap-classified.json.
 *   - 24 legacy codes (UPPERCASE) — the French model nomenclature used by
 *     apps/web/src/lib/referential/ngap.ts.
 *
 * Seven workspaces (cardiologie, pediatrie, ophtalmologie, psychiatrie,
 * endocrinologie, hematologie, rhumatologie) exist in the legacy vocabulary
 * but have no catalogue act of their own; their acts are billed inside
 * another scope. `catalogueActs: 0` records that honestly instead of
 * inventing a catalogue entry.
 */
import type { SpecialtyDefinition } from "./types";
import medecine_generale from "./specialties/medecine-generale";
import cardiologie from "./specialties/cardiologie";
import dermatologie from "./specialties/dermatologie";
import pediatrie from "./specialties/pediatrie";
import gynecologie from "./specialties/gynecologie";
import urgences from "./specialties/urgences";
import chirurgie_orthopedique from "./specialties/chirurgie-orthopedique";
import chirurgie_viscerale from "./specialties/chirurgie-viscerale";
import neurochirurgie from "./specialties/neurochirurgie";
import urologie from "./specialties/urologie";
import orl from "./specialties/orl";
import pneumologie from "./specialties/pneumologie";
import nephrologie from "./specialties/nephrologie";
import radiologie from "./specialties/radiologie";
import radiotherapie from "./specialties/radiotherapie";
import biologie from "./specialties/biologie";
import kinesitherapie from "./specialties/kinesitherapie";
import ophtalmologie from "./specialties/ophtalmologie";
import psychiatrie from "./specialties/psychiatrie";
import endocrinologie from "./specialties/endocrinologie";
import hematologie from "./specialties/hematologie";
import rhumatologie from "./specialties/rhumatologie";

/** Every specialty, in clinical order (general medicine first). */
export const SPECIALTIES: readonly SpecialtyDefinition[] = [
  medecine_generale,
  cardiologie,
  dermatologie,
  pediatrie,
  gynecologie,
  urgences,
  chirurgie_orthopedique,
  chirurgie_viscerale,
  neurochirurgie,
  urologie,
  orl,
  pneumologie,
  nephrologie,
  radiologie,
  radiotherapie,
  biologie,
  kinesitherapie,
  ophtalmologie,
  psychiatrie,
  endocrinologie,
  hematologie,
  rhumatologie,
] as const;

/** Canonical slugs, in the same order. */
export const SPECIALTY_SLUGS: readonly string[] = SPECIALTIES.map((s) => s.id);
